import React, { FormEvent, useEffect, useState } from 'react';
import { GAME_MODES, GAME_SKINS } from '../data/siteContent';
import { useSiteContext } from '../context/SiteContext';

const ProfilePage: React.FC = () => {
  const { isSignedIn, preferences, profile, signIn, signOut, signUp, updateProfile } =
    useSiteContext();
  const [feedback, setFeedback] = useState('');
  const [signInEmail, setSignInEmail] = useState(profile?.email || '');
  const [formState, setFormState] = useState({
    nickname: profile?.nickname || '',
    email: profile?.email || '',
    tagline: profile?.tagline || '',
    favoriteModeId: profile?.favoriteModeId || preferences.activeModeId,
    favoriteSkinId: profile?.favoriteSkinId || preferences.activeSkinId
  });

  useEffect(() => {
    setSignInEmail(profile?.email || '');
    setFormState({
      nickname: profile?.nickname || '',
      email: profile?.email || '',
      tagline: profile?.tagline || '',
      favoriteModeId: profile?.favoriteModeId || preferences.activeModeId,
      favoriteSkinId: profile?.favoriteSkinId || preferences.activeSkinId
    });
  }, [preferences.activeModeId, preferences.activeSkinId, profile]);

  const submitProfile = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    if (!formState.nickname.trim() || !formState.email.trim()) {
      setFeedback('Nickname and email are required.');
      return;
    }

    if (profile) {
      updateProfile(formState);
      setFeedback('Profile updated successfully.');
      return;
    }

    signUp(formState);
    setFeedback('Profile created and signed in.');
  };

  const submitSignIn = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    if (signIn(signInEmail)) {
      setFeedback('Signed in successfully.');
      return;
    }

    setFeedback('That email does not match the saved profile.');
  };

  return (
    <div className="page">
      <section className="page-heading">
        <div>
          <p className="eyebrow">Profile and authorization</p>
          <h1>Set up your player identity.</h1>
          <p>
            Create an account profile, keep your preferences, and personalize how you appear
            across the site.
          </p>
        </div>
        <div className="page-heading__meta">
          <span className="site-chip">{isSignedIn ? 'Signed in' : 'Signed out'}</span>
          <span className="site-chip">
            Profile: {profile ? profile.nickname : 'Not created yet'}
          </span>
        </div>
      </section>

      <section className="profile-layout">
        <article className="control-panel">
          <div className="section-heading section-heading--compact">
            <p className="eyebrow">{profile ? 'Edit profile' : 'Create profile'}</p>
            <h2>{profile ? 'Manage your account' : 'Create your account'}</h2>
          </div>

          <form className="site-form" onSubmit={submitProfile}>
            <label>
              Nickname
              <input
                type="text"
                value={formState.nickname}
                onChange={(event) =>
                  setFormState((currentValue) => ({
                    ...currentValue,
                    nickname: event.target.value
                  }))
                }
                placeholder="Arcade alias"
              />
            </label>

            <label>
              Email
              <input
                type="email"
                value={formState.email}
                onChange={(event) =>
                  setFormState((currentValue) => ({
                    ...currentValue,
                    email: event.target.value
                  }))
                }
                placeholder="player@example.com"
              />
            </label>

            <label>
              Tagline
              <textarea
                rows={3}
                value={formState.tagline}
                onChange={(event) =>
                  setFormState((currentValue) => ({
                    ...currentValue,
                    tagline: event.target.value
                  }))
                }
                placeholder="Fast hands. Cleaner turns."
              />
            </label>

            <label>
              Favorite mode
              <select
                value={formState.favoriteModeId}
                onChange={(event) =>
                  setFormState((currentValue) => ({
                    ...currentValue,
                    favoriteModeId: event.target.value
                  }))
                }
              >
                {GAME_MODES.map((mode) => (
                  <option key={mode.id} value={mode.id}>
                    {mode.name}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Favorite skin
              <select
                value={formState.favoriteSkinId}
                onChange={(event) =>
                  setFormState((currentValue) => ({
                    ...currentValue,
                    favoriteSkinId: event.target.value
                  }))
                }
              >
                {GAME_SKINS.map((skin) => (
                  <option key={skin.id} value={skin.id}>
                    {skin.name}
                  </option>
                ))}
              </select>
            </label>

            <button type="submit" className="button button--primary">
              {profile ? 'Save profile' : 'Create profile'}
            </button>
          </form>
        </article>

        <article className="control-panel control-panel--muted">
          <div className="section-heading section-heading--compact">
            <p className="eyebrow">Sign in</p>
            <h2>Access your profile</h2>
          </div>

          <form className="site-form" onSubmit={submitSignIn}>
            <label>
              Email
              <input
                type="email"
                value={signInEmail}
                onChange={(event) => setSignInEmail(event.target.value)}
                placeholder="Use your account email"
              />
            </label>

            <button type="submit" className="button button--secondary">
              Sign in
            </button>
          </form>

          {isSignedIn ? (
            <button type="button" className="button button--ghost" onClick={signOut}>
              Sign out
            </button>
          ) : null}

          <p className="inline-copy">
            Manage your nickname, favorites, and account details from one place.
          </p>

          {feedback ? <p className="inline-feedback">{feedback}</p> : null}
        </article>
      </section>
    </div>
  );
};

export default ProfilePage;
